'use client'
import { FC, ReactElement, useEffect, useRef, useState } from 'react'

import { BodySize, Colour, SectionPaddingDefault } from '../constants'

import { Link } from '../Link'

import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import {
  BLOCKS,
  INLINES,
  Table,
  TableRow,
  TableCell,
  Hyperlink,
} from '@contentful/rich-text-types'
import InlineSVG from 'react-inlinesvg'
import tickMettlePlus from '@/assets/icons/tick-mettle-plus.svg'
import tick from '@/assets/icons/tick-outlined.svg'
import cross from '@/assets/icons/cross-outlined.svg'
import tickWhite from '@/assets/icons/tick-outlined-white.svg'
import crossWhite from '@/assets/icons/cross-outlined-white.svg'
import mettleLogo from '@/assets/logos/mettle-table-logo.svg'
import mettlePlusLogo from '@/assets/logos/mettle-plus-table-logo.svg'
import styles from './styles.module.scss'
import cx from 'classnames'
import { PanelTitles } from '../PanelTitles'
import { useIsBlogPage } from '../../hooks/useIsBlogPage'

import arrowLeft from '../../assets/icons/arrow-left-white.svg'
import arrowRight from '../../assets/icons/arrow-right-white.svg'
import throttle from 'lodash/throttle'
import { Panel, PanelItem, PanelLayout } from '../Panel'
import { IconographyButton } from '../IconographyButton/IconographyButton'
import { CmsPageSectionBase } from '../ComposedPage/types'
import { Document } from '@contentful/rich-text-types'

export interface CmsComparisonTableSection extends CmsPageSectionBase {
  fields: CmsPageSectionBase['fields'] & {
    title?: string
    subtitle?: string
    ctaText?: string
    ctaUrl?: string
    backgroundColour?: Colour
    darkMode: boolean
    tableHeaderColour?: Colour
    darkenSecondColumn: boolean
    lockSecondColumn: boolean
    tintFirstColumn: boolean
    tableData: Document
    reducedWidth?: boolean
  }
}

export const ComparisonTableSection: FC<CmsComparisonTableSection> = ({
  fields,
}) => {
  const {
    backgroundColour = Colour.warmStone,
    tableHeaderColour = Colour.olive20,
    title,
    subtitle,
    ctaText,
    ctaUrl,
    tableData,
    sectionPaddingTop,
    sectionPaddingBottom,
    darkenSecondColumn = false,
    lockSecondColumn = false,
    tintFirstColumn = false,
    reducedWidth,
    darkMode = false,
  } = fields
  const THEME_COLOUR = darkMode ? Colour.black : backgroundColour
  const MAX_DESKTOP_COLUMNS = 5
  const MAX_TABLET_COLUMNS = 4
  const MAX_MOBILE_COLUMNS = 3
  const tableRef = useRef<null | HTMLDivElement>(null)
  const isBlog = useIsBlogPage()
  const options = {
    renderNode: {
      [BLOCKS.TABLE]: (_: Table, children: ReactElement[]) => (
        <table>
          <tbody>{children}</tbody>
        </table>
      ),
      [BLOCKS.TABLE_ROW]: (_: TableRow, children: ReactElement[]) => (
        <tr>{children}</tr>
      ),
      [BLOCKS.TABLE_CELL]: (_: TableCell, children: ReactElement[]) => (
        <td>{children}</td>
      ),
      [BLOCKS.TABLE_HEADER_CELL]: (_: TableCell, children: ReactElement[]) => (
        <th>{children}</th>
      ),
      [INLINES.HYPERLINK]: (
        { data: { uri } }: Hyperlink,
        children: ReactElement,
      ) => {
        return (
          <Link secondary url={uri}>
            {children}
          </Link>
        )
      },
    },
    renderText: (text: string) => {
      if (text && /:tick:/.test(text)) {
        return (
          <InlineSVG
            description="checkmark"
            src={darkMode ? tickWhite.src : tick.src}
          />
        )
      } else if (text && /:tick-mettle-plus:/.test(text)) {
        return <InlineSVG description="checkmark" src={tickMettlePlus.src} />
      } else if (text && /:cross:/.test(text)) {
        return (
          <InlineSVG
            description="cross"
            src={darkMode ? crossWhite.src : cross.src}
          />
        )
      } else if (text && /:mettle-logo:/.test(text)) {
        return (
          <InlineSVG
            className={styles.logo}
            description="mettle logo"
            src={mettleLogo.src}
          />
        )
      } else if (text && /:mettle-plus-logo:/.test(text)) {
        return (
          <InlineSVG
            className={styles.logo}
            description="mettle plus logo"
            src={mettlePlusLogo.src}
          />
        )
      } else {
        return text
      }
    },
  }
  const [tableState, setTableState] = useState({
    maxDisplayColumns: 0,
    windowWidth: 0,
    position: 0,
    maxPosition: 0,
    rowLength: 0,
    displayControls: false,
  })

  const getMaxDisplayColumns = () => {
    const width = window.innerWidth

    if (width > BodySize.maxTablet) {
      return MAX_DESKTOP_COLUMNS
    } else if (width <= BodySize.maxTablet && width > BodySize.maxMobile) {
      return MAX_TABLET_COLUMNS
    } else {
      return MAX_MOBILE_COLUMNS
    }
  }

  const getRowLength = () => {
    const el = tableRef.current
    const table = el?.querySelector('table')
    const rows = table?.querySelectorAll('tr')
    let rowLength = 0

    if (rows) {
      rowLength = rows[0].querySelectorAll('th, td').length
    }

    return rowLength
  }

  const getMaxPosition = () => {
    const maxDisplayColumns = getMaxDisplayColumns()

    return getRowLength() - maxDisplayColumns
  }

  const shouldDisplayControls = (rowLength: number) => {
    const width = window.innerWidth

    if (width > BodySize.maxTablet) {
      return rowLength > MAX_DESKTOP_COLUMNS
    } else if (width <= BodySize.maxTablet && width > BodySize.maxMobile) {
      return rowLength > MAX_TABLET_COLUMNS
    } else {
      return rowLength > MAX_MOBILE_COLUMNS
    }
  }

  const configureForWidth = () => {
    if (window.innerWidth !== tableState.windowWidth) {
      const rowLength = getRowLength()

      setTableState(prev => {
        return {
          ...prev,
          position: 0,
          windowWidth: window.innerWidth,
          maxDisplayColumns: getMaxDisplayColumns(),
          maxPosition: getMaxPosition(),
          rowLength: rowLength,
          displayControls: shouldDisplayControls(rowLength),
        }
      })
    }
  }

  const updateTableDisplay = () => {
    const frozenColumns = lockSecondColumn ? 2 : 1
    const position = tableState.position

    if (tableRef.current) {
      const el = tableRef.current
      const table = el?.querySelector('table')
      const rows = table?.querySelectorAll('tr')

      rows?.forEach(row => {
        const cells = row.querySelectorAll('td ,th')

        cells?.forEach((cell, index) => {
          if (
            (index >= frozenColumns && index - position < frozenColumns) ||
            index - position > tableState.maxDisplayColumns - 1
          ) {
            cell.classList.add(styles.hidden)
            cell.classList.remove(styles.show)
          } else {
            cell.classList.add(styles.show)
            cell.classList.remove(styles.hidden)
          }

          if (
            index - position === tableState.maxDisplayColumns - 1 ||
            index === tableState.rowLength - 1
          ) {
            cell.classList.add(styles.last)
          } else {
            cell.classList.remove(styles.last)
          }
        })
      })
    }
  }

  useEffect(() => {
    const callback = throttle(configureForWidth, 100)

    window.addEventListener('resize', callback)

    return () => window.removeEventListener('resize', callback)
    // oxlint-disable-next-line
  }, [tableState.windowWidth])

  useEffect(
    configureForWidth,
    // oxlint-disable-next-line
    [],
  )
  useEffect(
    updateTableDisplay,
    // oxlint-disable-next-line
    [
      tableState.windowWidth,
      tableState.position,
      tableState.maxDisplayColumns,
      tableState.rowLength,
    ],
  )

  return (
    <Panel
      className={cx(styles.comparisonTable, {
        [styles.darkMode]: darkMode,
      })}
      colour={Colour[THEME_COLOUR]}
      layout={PanelLayout.full}
      short
    >
      <PanelItem
        className={cx(
          styles.item,
          styles[sectionPaddingTop || SectionPaddingDefault.TOP],
          styles[sectionPaddingBottom || SectionPaddingDefault.BOTTOM],
          { [styles.isBlog]: Boolean(isBlog) },
        )}
      >
        <PanelTitles
          title={title}
          subtitle={subtitle}
          ctaText={ctaText}
          ctaUrl={ctaUrl}
          className={tableState.displayControls ? styles.noBottomMargin : ''}
        />
        <div
          className={cx(styles.content, {
            [styles.reducedWidth]: reducedWidth,
          })}
        >
          {tableState.displayControls ? (
            <div className={cx(styles.controls, styles[THEME_COLOUR])}>
              <IconographyButton
                value="Back"
                hollow
                icon={arrowLeft}
                handler={() =>
                  setTableState(prev => {
                    return { ...prev, position: prev.position - 1 }
                  })
                }
                disabled={tableState.position === 0}
              />
              <IconographyButton
                value="Forward"
                icon={arrowRight}
                handler={() =>
                  setTableState(prev => {
                    return { ...prev, position: prev.position + 1 }
                  })
                }
                disabled={tableState.position === tableState.maxPosition}
              />
            </div>
          ) : (
            <div
              className={cx(styles.spacer, styles[backgroundColour], {
                [styles.darkMode]: Boolean(darkMode),
              })}
            />
          )}
          <div
            data-testid="table-container"
            className={cx(styles.tableContainer, {
              [styles.hasControls]: tableState.displayControls,
              [styles.darkSecondColumn]: darkenSecondColumn,
              [styles.tintFirstColumn]: tintFirstColumn,
              [styles.olive20]: tableHeaderColour === Colour.olive20,
              [styles.teal20]: tableHeaderColour === Colour.teal20,
            })}
          >
            {tableData && (
              <div ref={tableRef}>
                {/* @ts-expect-error - Type mismatch between Contentful types and react-renderer, options wants generic Block/Inline and React.ReactNode */}
                {documentToReactComponents(tableData, options)}
              </div>
            )}
          </div>
        </div>
      </PanelItem>
    </Panel>
  )
}
